import {
  forwardRef,
  type ButtonHTMLAttributes,
  type FunctionComponent,
  type SVGProps,
} from "react";

import { APP_COLORS } from "@styles/colors";
import { cn } from "@utils/tailwind.util";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  isIconOnly?: boolean;
  buttonSize?: "MEDIUM" | "SMALL";
  iconSize?: string;
  isDisabled?: boolean;
  buttonType: "PRIMARY" | "SECONDARY" | "CANCEL" | "TRANSPARENT" | "TERTIARY" | "DANGER";
  PrefixIcon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  SuffixIcon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  PrefixIconClass?: string;
  SuffixIconClass?: string;
  strokeWidthIcon?: number | string;
  disableActiveState?: boolean;
}

const GRADIENTS: Record<ButtonProps["buttonType"], string | undefined> = {
  PRIMARY: "linear-gradient(180deg, rgba(255, 255, 255, 0.10) 0%, rgba(0, 0, 0, 0.10) 100%)",
  DANGER: "linear-gradient(180deg, rgba(255, 255, 255, 0.10) 0%, rgba(0, 0, 0, 0.10) 100%)",
  SECONDARY: "linear-gradient(180deg, rgba(255, 255, 255, 0.02) 0%, rgba(0, 0, 0, 0.02) 100%)",
  CANCEL: "linear-gradient(180deg, rgba(255, 255, 255, 0.02) 0%, rgba(0, 0, 0, 0.02) 100%)",
  TERTIARY:
    "linear-gradient(180deg, rgba(255, 255, 255, 0.02) 0%, rgba(0, 0, 0, 0.02) 100%), #393D46",
  TRANSPARENT: undefined,
};

const ButtonIcon = ({
  className,
  icon: Icon,
  iconSize = "20px",
  buttonType,
  strokeWidth,
}: {
  icon: FunctionComponent<SVGProps<SVGSVGElement>>;
  className?: string;
  iconSize?: string;
  buttonType: ButtonProps["buttonType"];
  strokeWidth?: number | string;
}) => {
  return (
    <Icon
      className={className}
      stroke={
        className
          ? "currentColor"
          : buttonType === "PRIMARY" || buttonType === "DANGER"
            ? APP_COLORS.invertedPrimary
            : APP_COLORS.gray[950]
      }
      strokeWidth={strokeWidth}
      width={iconSize}
      height={iconSize}
    />
  );
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className = "",
      PrefixIcon,
      PrefixIconClass,
      SuffixIcon,
      SuffixIconClass,
      iconSize,
      isIconOnly = false,
      isDisabled = false,
      buttonSize = "MEDIUM",
      buttonType = "PRIMARY",
      strokeWidthIcon,
      style = {},
      disableActiveState = false,
      ...restHtmlButtonProps
    },
    ref
  ) => {
    return (
      <button
        disabled={isDisabled}
        className={cn(
          "flex items-center cursor-pointer justify-center gap-1 rounded-lg border border-transparent bg-origin-border font-inter text-sm font-medium not-italic shadow-mainButton transition duration-150 ease-in-out focus-visible:outline-0",
          {
            "p-2": isIconOnly,
            "px-3.5 py-2": !isIconOnly,
            "pl-2.5": PrefixIcon && !isIconOnly,
            "pr-2.5": SuffixIcon && !isIconOnly,
            "pl-2 pr-2": isIconOnly && (PrefixIcon || SuffixIcon),
            "opacity-40 cursor-default": isDisabled,
            "bg-action-primary text-content-text hover:enabled:bg-action-primaryHover focus:enabled:border-stroke-actionPrimary focus:enabled:bg-action-primaryHover focus:enabled:shadow-mainButtonFocus active:enabled:shadow-mainButtonPressed":
              buttonType === "PRIMARY",
            [`bg-action-secondary text-content-primary hover:enabled:bg-action-secondaryHover ${
              !disableActiveState
                ? "focus:enabled:border-stroke-actionPrimary focus:enabled:bg-action-secondaryHover focus:enabled:shadow-mainButtonFocus active:enabled:border-transparent active:enabled:bg-action-secondaryPressed active:enabled:shadow-secondaryButtonPressed"
                : ""
            }`]: buttonType === "SECONDARY" || buttonType === "CANCEL",
            "text-gray-800 border-gray-325 dark:bg-gray-700 dark:text-invertedPrimary80 dark:hover:bg-gray-600":
              buttonType === "CANCEL",
            "bg-[#393D46] text-white hover:enabled:bg-[#393D46]/[0.8] focus:enabled:border-stroke-actionPrimary focus:enabled:bg-[#393D46]/[0.8] focus:enabled:shadow-mainButtonFocus active:enabled:border-transparent active:enabled:bg-[#393D46]/[0.8] active:enabled:shadow-secondaryButtonPressed":
              buttonType === "TERTIARY",
            "bg-transparent text-teal-600 shadow-none hover:enabled:bg-action-secondaryHover focus:enabled:border-stroke-actionPrimary focus:enabled:bg-action-secondaryHover active:enabled:bg-action-secondaryPressed active:enabled:shadow-secondaryButtonPressed":
              buttonType === "TRANSPARENT",
            "bg-action-danger text-content-text shadow-dangerButton hover:enabled:bg-action-dangerHover focus:enabled:border-stroke-actionPrimary focus:enabled:bg-action-dangerHover focus:enabled:shadow-mainButtonFocus active:enabled:shadow-mainButtonPressed":
              buttonType === "DANGER",
            "h-[36px]": buttonSize === "MEDIUM",
            "min-h-[28px] min-w-[28px]": buttonSize === "SMALL",
            "min-w-[36px]": buttonSize !== "SMALL" && buttonSize !== "MEDIUM",
          },
          className
        )}
        style={{ ...style, backgroundImage: GRADIENTS[buttonType] }}
        {...restHtmlButtonProps}
        ref={ref}
      >
        {PrefixIcon && (
          <ButtonIcon
            icon={PrefixIcon}
            strokeWidth={strokeWidthIcon}
            className={PrefixIconClass}
            iconSize={iconSize}
            buttonType={buttonType}
          />
        )}
        {children}
        {SuffixIcon && (
          <ButtonIcon
            icon={SuffixIcon}
            strokeWidth={strokeWidthIcon}
            className={SuffixIconClass}
            iconSize={iconSize}
            buttonType={buttonType}
          />
        )}
      </button>
    );
  }
);
